/** @format */

import { useState, useMemo, useCallback, useEffect } from "react";
import { User, AuthContext } from "./AuthContext";
import { trackEvent } from "../../utils/analytics";
import { onSessionExpired } from "../../api/session";
import { SessionExpiredDialog } from "../../components/SessionExpiredDialog";

const AUTH_URL = import.meta.env.VITE_AUTH_URL;
const AUTH_API_URL = import.meta.env.VITE_AUTH_API_URL;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
	const [currentUser, setCurrentUser] = useState<User | null>(null);
	const [isAuthenticated, setAuthenticated] = useState(false);
	const [isLoading, setLoading] = useState(true);
	const [isSessionExpired, setSessionExpired] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// The user stays "authenticated" so protected routes keep rendering their
	// work behind the dialog instead of redirecting straight to the login portal.
	useEffect(() => onSessionExpired(() => setSessionExpired(true)), []);

	useEffect(() => {
		let isCancelled = false;
		const fetchMe = async () => {
			try {
				const response = await fetch(`${AUTH_API_URL}/me`, {
					method: "GET",
					credentials: "include",
				});
				if (isCancelled) {
					return;
				}
				if (response.status === 401 || response.status === 403) {
					setCurrentUser(null);
					setAuthenticated(false);
					return;
				}
				if (!response.ok) {
					throw new Error(`Failed to load user (${response.status})`);
				}
				const user: User = await response.json();
				setCurrentUser(user);
				setAuthenticated(true);
			} catch (err) {
				if (isCancelled) {
					return;
				}
				setError(err instanceof Error ? err.message : "Failed to load user");
				setCurrentUser(null);
				setAuthenticated(false);
			} finally {
				if (!isCancelled) {
					setLoading(false);
				}
			}
		};
		void fetchMe();
		return () => {
			isCancelled = true;
		};
	}, []);

	const login = useCallback(() => {
		trackEvent("login");
		const returnTo = globalThis.location.href;
		globalThis.location.assign(`${AUTH_URL}/?r=${encodeURIComponent(returnTo)}`);
	}, []);

	const logout = useCallback(async () => {
		trackEvent("logout");
		try {
			await fetch(`${AUTH_API_URL}/auth/logout`, {
				method: "POST",
				credentials: "include",
			});
		} catch (err) {
			setError(err instanceof Error ? err.message : "Logout failed");
		} finally {
			setCurrentUser(null);
			setAuthenticated(false);
		}
	}, []);

	const value = useMemo(
		() => ({
			user: currentUser,
			login,
			logout,
			isAuthenticated,
			isLoading,
			isSessionExpired,
			error,
		}),
		[currentUser, login, logout, isAuthenticated, isLoading, isSessionExpired, error],
	);

	return (
		<AuthContext.Provider value={value}>
			{children}
			{isSessionExpired && <SessionExpiredDialog onLogin={login} onDismiss={() => setSessionExpired(false)} />}
		</AuthContext.Provider>
	);
};
